import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import {
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

export interface MetaSummaryByDayNoDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** day_no-based loader for META/HOPA charts.
 */
export class MetaSummaryByDayNoDataLoaderFactory extends Factory<
    DataLoader,
    MetaSummaryByDayNoDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'MetaSummaryByDayNoDataLoaderFactory';

    getInstance(options: MetaSummaryByDayNoDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardUsersActivity.sqlMap;

                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const maxDayNo =
                    queryContext.settings?.[
                        QueryContextSettingID.RETENTION_MAX_DAY_NO
                    ] ??
                    QueryContextDefaultSettings[
                        QueryContextSettingID.RETENTION_MAX_DAY_NO
                    ]!;
                const maxDayNoFilter =
                    maxDayNo !== 'unlimited'
                        ? `AND install_day_no <= ${Number(maxDayNo)}`
                        : '';

                const { splitSelect, splitGroupBy, where, join } =
                    BiVizDashboardUsersActivity.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options
                    );

                const query = `
-- DataLoaderId: ${this.instanceId}

SELECT
  ${splitSelect}install_day_no AS day_no,
  -- "per active user": all active users count, no deco data = 0
  -- players that do not place any decos deliberately drag the stats down.
  APPROX_QUANTILES(COALESCE(new_decos_placed, 0), 100)[OFFSET(50)] AS median_decos_placed,
  AVG(COALESCE(new_decos_placed, 0)) AS avg_decos_placed,
FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                )}\` ds
${join}
${where}
  ${maxDayNoFilter}
GROUP BY ${splitGroupBy}day_no
ORDER BY ${splitGroupBy}day_no ASC
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                this.emitter.emit({
                    data: this.latestData,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
